export type CsvColumnType =
  | "ignore"
  | "first_name"
  | "last_name"
  | "full_name"
  | "phone"
  | "email"
  | "company"
  | "website"
  | "url"
  | "address"
  | "city"
  | "state"
  | "postal_code"
  | "source"
  | "tags"
  | "custom"
  | "custom_url"
  | "custom_number"
  | "custom_date"
  | "custom_boolean"
  | "custom_select"
  | "custom_checkbox"
  | "custom_radio";

export type CsvColumnMapping = {
  header: string;
  type: CsvColumnType;
  customKey?: string;
  customLabel?: string;
  customOptions?: string[];
  customUnique?: boolean;
  trackableUrl?: boolean;
};

export type CsvImportPreview = {
  headers: string[];
  rowCount: number;
  sampleRows: Record<string, string>[];
  acceptedEstimate: number;
  duplicateEstimate: number;
  rejectedEstimate: number;
  unmappedCount: number;
  customFieldCount: number;
  trackingUrlFieldCount: number;
  duplicateCoreFields: CsvColumnType[];
  invalidCustomKeys: string[];
  fieldSummaries: Array<{
    header: string;
    type: CsvColumnType;
    customKey?: string;
    sampleValues: string[];
    suggested: boolean;
  }>;
};

export const csvColumnTypeLabels: Array<{ type: CsvColumnType; label: string }> = [
  { type: "ignore", label: "Do not import" },
  { type: "first_name", label: "First name" },
  { type: "last_name", label: "Last name" },
  { type: "full_name", label: "Full name" },
  { type: "phone", label: "Phone number" },
  { type: "email", label: "Email" },
  { type: "company", label: "Company" },
  { type: "website", label: "Website" },
  { type: "url", label: "URL" },
  { type: "address", label: "Address" },
  { type: "city", label: "City" },
  { type: "state", label: "State" },
  { type: "postal_code", label: "Postal code" },
  { type: "source", label: "Source" },
  { type: "tags", label: "Tags" },
  { type: "custom", label: "Custom field" },
  { type: "custom_url", label: "Custom tracking URL" },
  { type: "custom_number", label: "Custom number" },
  { type: "custom_date", label: "Custom date" },
  { type: "custom_boolean", label: "Custom true/false" },
  { type: "custom_select", label: "Custom select" },
  { type: "custom_checkbox", label: "Custom checkbox group" },
  { type: "custom_radio", label: "Custom radio group" },
];

const exclusiveCoreFields = new Set<CsvColumnType>([
  "first_name",
  "last_name",
  "full_name",
  "phone",
  "email",
  "company",
  "website",
  "url",
  "address",
  "city",
  "state",
  "postal_code",
  "source",
  "tags",
]);

function normalizeHeader(header: string): string {
  return header
    .trim()
    .replace(/\uFEFF/g, "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

function isCustomColumnType(type: CsvColumnType): boolean {
  return type === "custom" || type.startsWith("custom_");
}

function customTypeForColumn(
  type: CsvColumnType,
): "text" | "url" | "number" | "date" | "boolean" | "select" | "checkbox" | "radio" {
  if (type === "custom_url") return "url";
  if (type === "custom_number") return "number";
  if (type === "custom_date") return "date";
  if (type === "custom_boolean") return "boolean";
  if (type === "custom_select") return "select";
  if (type === "custom_checkbox") return "checkbox";
  if (type === "custom_radio") return "radio";

  return "text";
}

export function toCustomFieldKey(header: string): string {
  const normalized = normalizeHeader(header);

  return normalized || "custom_field";
}

function splitCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && quoted && next === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      quoted = !quoted;
      continue;
    }

    if (char === "," && !quoted) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());

  return values;
}

function csvLines(csv: string): string[] {
  return csv
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function detectColumnType(header: string): CsvColumnType {
  const normalized = normalizeHeader(header);

  if (["first_name", "firstname", "given_name"].includes(normalized)) return "first_name";
  if (["last_name", "lastname", "surname", "family_name"].includes(normalized)) return "last_name";
  if (["name", "full_name", "display_name", "contact_name"].includes(normalized))
    return "full_name";
  if (["phone", "phone_number", "mobile", "cell", "telephone"].includes(normalized)) return "phone";
  if (["email", "email_address", "mail"].includes(normalized)) return "email";
  if (["company", "company_name", "business", "organization"].includes(normalized))
    return "company";
  if (["website", "site", "business_website"].includes(normalized)) return "website";
  if (["url", "link", "landing_page"].includes(normalized)) return "url";
  if (normalized.endsWith("_url") || normalized.includes("profile_url")) return "custom_url";
  if (
    normalized.endsWith("_date") ||
    normalized.includes("appointment_date") ||
    normalized.includes("created_at") ||
    normalized.includes("updated_at")
  )
    return "custom_date";
  if (
    normalized.startsWith("is_") ||
    normalized.startsWith("has_") ||
    normalized.includes("active") ||
    normalized.includes("eligible")
  )
    return "custom_boolean";
  if (
    normalized.endsWith("_count") ||
    normalized.endsWith("_score") ||
    normalized.includes("amount") ||
    normalized.includes("revenue")
  )
    return "custom_number";
  if (["address", "street_address"].includes(normalized)) return "address";
  if (["city", "locality"].includes(normalized)) return "city";
  if (["state", "province", "region"].includes(normalized)) return "state";
  if (["postal_code", "postcode", "zip", "zip_code"].includes(normalized)) return "postal_code";
  if (["source", "lead_source", "attribution_source", "attribution"].includes(normalized)) {
    return "source";
  }
  if (["tags", "tag", "labels"].includes(normalized)) return "tags";

  return "custom";
}

export function defaultCsvColumnMappings(headers: string[]): CsvColumnMapping[] {
  return headers.map((header) => {
    const type = detectColumnType(header);

    return {
      header,
      type,
      customKey: isCustomColumnType(type) ? toCustomFieldKey(header) : undefined,
      customLabel: isCustomColumnType(type) ? header : undefined,
      trackableUrl: type === "custom_url" ? true : undefined,
    };
  });
}

function valueForType(
  row: Record<string, string>,
  mappings: CsvColumnMapping[],
  type: CsvColumnType,
): string | undefined {
  const mapping = mappings.find((entry) => entry.type === type);

  return mapping ? row[mapping.header]?.trim() || undefined : undefined;
}

function rowDedupeKey(
  row: Record<string, string>,
  mappings: CsvColumnMapping[],
): string | undefined {
  const email = valueForType(row, mappings, "email")?.toLowerCase();
  const phone = valueForType(row, mappings, "phone")?.replace(/[^\d+]/g, "");

  if (email) return `email:${email}`;
  if (phone) return `phone:${phone}`;

  const fullName =
    valueForType(row, mappings, "full_name") ||
    [valueForType(row, mappings, "first_name"), valueForType(row, mappings, "last_name")]
      .filter(Boolean)
      .join(" ");

  return fullName ? `name:${fullName.toLowerCase()}` : undefined;
}

function displayNameForRow(row: Record<string, string>, mappings: CsvColumnMapping[]): string {
  const splitName = [
    valueForType(row, mappings, "first_name"),
    valueForType(row, mappings, "last_name"),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    valueForType(row, mappings, "full_name") ||
    splitName ||
    valueForType(row, mappings, "company") ||
    valueForType(row, mappings, "email") ||
    valueForType(row, mappings, "phone") ||
    ""
  );
}

export function analyzeCsvImport(csv: string, mappings?: CsvColumnMapping[]): CsvImportPreview {
  const lines = csvLines(csv);
  const headers = splitCsvLine(lines[0] ?? "").filter(Boolean);
  const rows = lines.slice(1).map((line) => {
    const values = splitCsvLine(line);

    return Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""]));
  });
  const resolvedMappings = mappings ?? defaultCsvColumnMappings(headers);
  const duplicateCoreFields = [...exclusiveCoreFields].filter(
    (type) => resolvedMappings.filter((mapping) => mapping.type === type).length > 1,
  );
  const customKeys = resolvedMappings
    .filter((mapping) => isCustomColumnType(mapping.type))
    .map((mapping) => mapping.customKey?.trim() ?? "");
  const invalidCustomKeys = customKeys.filter(
    (key, index) => !/^[a-z][a-z0-9_]*$/.test(key) || customKeys.indexOf(key) !== index,
  );
  const seenKeys = new Set<string>();
  let duplicateEstimate = 0;
  let rejectedEstimate = 0;

  for (const row of rows) {
    const dedupeKey = rowDedupeKey(row, resolvedMappings);
    const hasIdentifier = Boolean(
      valueForType(row, resolvedMappings, "phone") || valueForType(row, resolvedMappings, "email"),
    );

    if (!displayNameForRow(row, resolvedMappings) || !hasIdentifier) {
      rejectedEstimate += 1;
      continue;
    }

    if (dedupeKey && seenKeys.has(dedupeKey)) {
      duplicateEstimate += 1;
      continue;
    }

    if (dedupeKey) {
      seenKeys.add(dedupeKey);
    }
  }

  return {
    headers,
    rowCount: rows.length,
    sampleRows: rows.slice(0, 3),
    acceptedEstimate: Math.max(rows.length - duplicateEstimate - rejectedEstimate, 0),
    duplicateEstimate,
    rejectedEstimate,
    unmappedCount: resolvedMappings.filter((mapping) => mapping.type === "ignore").length,
    customFieldCount: resolvedMappings.filter((mapping) => isCustomColumnType(mapping.type)).length,
    trackingUrlFieldCount: resolvedMappings.filter((mapping) => {
      return (
        mapping.type === "url" || mapping.type === "custom_url" || Boolean(mapping.trackableUrl)
      );
    }).length,
    duplicateCoreFields,
    invalidCustomKeys,
    fieldSummaries: resolvedMappings.map((mapping) => {
      const detected = detectColumnType(mapping.header);

      return {
        header: mapping.header,
        type: mapping.type,
        customKey: mapping.customKey,
        sampleValues: rows
          .map((row) => row[mapping.header]?.trim())
          .filter((value): value is string => Boolean(value))
          .slice(0, 3),
        suggested:
          mapping.type === detected &&
          (isCustomColumnType(mapping.type)
            ? mapping.customKey === toCustomFieldKey(mapping.header)
            : true),
      };
    }),
  };
}

export function csvMappingHasBlockingErrors(preview: CsvImportPreview): boolean {
  return preview.duplicateCoreFields.length > 0 || preview.invalidCustomKeys.length > 0;
}

export function buildCrmColumnMap(mappings: CsvColumnMapping[]): Record<string, string> {
  const byType = (type: CsvColumnType) => mappings.find((mapping) => mapping.type === type)?.header;
  const columnMap: Record<string, string> = {};
  const fullName = byType("full_name");
  const firstName = byType("first_name");
  const lastName = byType("last_name");

  if (fullName) columnMap.displayName = fullName;
  if (firstName) columnMap.firstName = firstName;
  if (lastName) columnMap.lastName = lastName;
  if (byType("phone")) columnMap.phone = byType("phone") ?? "";
  if (byType("email")) columnMap.email = byType("email") ?? "";
  if (byType("company")) columnMap.company = byType("company") ?? "";
  if (byType("tags")) columnMap.tags = byType("tags") ?? "";
  if (byType("source")) columnMap.attributionSource = byType("source") ?? "";

  return columnMap;
}

export function buildCsvMappingMetadata(mappings: CsvColumnMapping[]) {
  return {
    columnMappings: mappings,
    customFields: mappings
      .filter((mapping) => isCustomColumnType(mapping.type))
      .map((mapping) => ({
        header: mapping.header,
        key: mapping.customKey,
        label: mapping.customLabel?.trim() || mapping.header,
        type: customTypeForColumn(mapping.type),
        options: mapping.customOptions ?? [],
        isUnique: Boolean(mapping.customUnique),
        trackingEligible: mapping.type === "custom_url" || Boolean(mapping.trackableUrl),
      })),
    trackingLinkEligibleColumns: mappings
      .filter((mapping) => {
        return (
          mapping.type === "url" || mapping.type === "custom_url" || Boolean(mapping.trackableUrl)
        );
      })
      .map((mapping) => mapping.header),
  };
}
